import React, { useState, useEffect, useMemo } from 'react';
import Sidebar from '../components/Sidebar';
import TopHeader from '../components/TopHeader';
import { accountingAPI } from '../utils/api';
import {
  BarChart3, Download, Calendar, ChevronLeft, TrendingUp, TrendingDown,
  FileText, PieChart, DollarSign, Target, ArrowRight, AlertTriangle,
  CheckCircle, Wallet
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

// Format currency
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount || 0);
};

// Format percentage
const formatPercent = (value) => {
  return `${(value || 0).toFixed(1)}%`;
};

// Category breakdown bar
const CategoryBar = ({ label, value, total, color = 'teal' }) => {
  const percentage = total > 0 ? (value / total) * 100 : 0;
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-sm">
        <span className="text-slate-600 capitalize">{label.replace('_', ' ')}</span>
        <span className="font-medium text-slate-800">{formatCurrency(value)}</span>
      </div>
      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
        <div 
          className={`h-full bg-${color}-500 rounded-full transition-all duration-500`}
          style={{ width: `${percentage}%`, backgroundColor: color === 'teal' ? '#14b8a6' : '#8b5cf6' }}
        />
      </div>
    </div>
  );
};

// Stat Card
const StatCard = ({ label, value, subvalue, icon: Icon, color = 'slate' }) => {
  const colorClasses = {
    green: 'text-green-600 bg-green-50',
    red: 'text-red-600 bg-red-50',
    blue: 'text-blue-600 bg-blue-50',
    orange: 'text-orange-600 bg-orange-50',
    slate: 'text-slate-600 bg-slate-50',
  };
  
  return (
    <div className="flex items-center gap-4 p-4 bg-white rounded-xl border border-slate-100">
      <div className={`p-3 rounded-xl ${colorClasses[color]}`}>
        <Icon size={20} />
      </div>
      <div>
        <p className="text-sm text-slate-500">{label}</p>
        <p className="text-xl font-bold text-slate-800">{value}</p>
        {subvalue && <p className="text-xs text-slate-400">{subvalue}</p>}
      </div>
    </div>
  );
};

export default function FinancialReports() {
  const navigate = useNavigate();
  const today = new Date();
  const yearStart = `${today.getFullYear()}-01-01`;
  const yearEnd = today.toISOString().split('T')[0];

  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState(yearStart);
  const [endDate, setEndDate] = useState(yearEnd);
  const [activeTab, setActiveTab] = useState('pnl');
  const [selectedMonth, setSelectedMonth] = useState(`${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`);
  
  // Report data
  const [pnlReport, setPnlReport] = useState(null);
  const [salesReport, setSalesReport] = useState(null);
  const [expenseReport, setExpenseReport] = useState(null);
  const [outstandingReport, setOutstandingReport] = useState(null);
  const [monthlyReport, setMonthlyReport] = useState(null);
  // Per-report error flags so UI can show "failed to load" instead of silent ₹0
  const [reportErrors, setReportErrors] = useState({});

  // Derived P&L metrics — single source of truth; falls back gracefully when data is loading
  const pnlMetrics = useMemo(() => {
    const prof = pnlReport?.profitability || {};
    const operatingProfit = prof.operating_profit ?? prof.net_profit ?? 0;
    const cashProfit       = prof.cash_profit ?? 0;
    const operatingMargin  = prof.operating_margin ?? prof.profit_margin ?? 0;
    const isProfitable     = prof.is_profitable !== undefined ? prof.is_profitable : operatingProfit >= 0;
    const breakevenGap     = prof.breakeven_gap ?? Math.max(0, -operatingProfit);
    const expenseCoverage  = prof.expense_coverage_ratio ?? 0;
    const cashMargin       = prof.cash_margin ?? 0;
    return { operatingProfit, cashProfit, operatingMargin, isProfitable, breakevenGap, expenseCoverage, cashMargin };
  }, [pnlReport]);

  // Fetch reports — each call is independent; errors are tracked per-report
  // so a single failing endpoint doesn't mask others and the UI shows a real error
  const fetchReports = async () => {
    setLoading(true);
    setReportErrors({});

    const settle = async (key, promise) => {
      try {
        const res = await promise;
        return { key, data: res.data, error: null };
      } catch (e) {
        const status = e?.response?.status;
        const msg = e?.response?.data?.detail || e?.message || 'Failed to load';
        console.error(`[${key}] ${status}: ${msg}`);
        return { key, data: null, error: `${status ? `${status}: ` : ''}${msg}` };
      }
    };

    const results = await Promise.all([
      settle('pnl',         accountingAPI.getPnLReport(startDate, endDate)),
      settle('sales',       accountingAPI.getSalesReport(startDate, endDate)),
      settle('expenses',    accountingAPI.getExpenseReport(startDate, endDate)),
      settle('outstanding', accountingAPI.getOutstandingReport(startDate, endDate)),
      settle('monthly',     accountingAPI.getMonthlyReport(selectedMonth)),
    ]);

    const errors = {};
    for (const r of results) {
      if (r.data !== null) {
        if (r.key === 'pnl')         setPnlReport(r.data);
        if (r.key === 'sales')       setSalesReport(r.data);
        if (r.key === 'expenses')    setExpenseReport(r.data);
        if (r.key === 'outstanding') setOutstandingReport(r.data);
        if (r.key === 'monthly')     setMonthlyReport(r.data);
      } else {
        errors[r.key] = r.error;
      }
    }
    setReportErrors(errors);
    setLoading(false);
  };

  useEffect(() => {
    fetchReports();
  }, [startDate, endDate, selectedMonth]);

  // Quick date presets
  const setPreset = (preset) => {
    const now = new Date();
    let start, end = now.toISOString().split('T')[0];
    
    switch (preset) {
      case 'today':
        start = end;
        break;
      case 'week':
        const weekStart = new Date(now);
        weekStart.setDate(now.getDate() - now.getDay());
        start = weekStart.toISOString().split('T')[0];
        break;
      case 'month':
        start = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
        break;
      case 'quarter':
        const qMonth = Math.floor(now.getMonth() / 3) * 3;
        start = `${now.getFullYear()}-${String(qMonth + 1).padStart(2, '0')}-01`;
        break;
      case 'year':
        start = `${now.getFullYear()}-01-01`;
        break;
      default:
        return;
    }
    
    setStartDate(start);
    setEndDate(end);
  };

  // Export report
  const handleExport = (type) => {
    let data, headers, filename;
    
    switch (type) {
      case 'sales':
        headers = ['Client', 'Product', 'Total', 'Received', 'Balance', 'Status', 'Date'];
        data = salesReport?.data?.map(s => [
          s.client_name, s.visa_type || s.product, s.total_amount, 
          s.amount_received, s.balance_due, s.payment_status, s.created_at?.slice(0, 10)
        ]) || [];
        filename = 'sales_report';
        break;
      case 'expenses':
        headers = ['Title', 'Category', 'Type', 'Amount', 'Date'];
        data = expenseReport?.data?.map(e => [
          e.title, e.category, e.expense_type, e.amount, e.date
        ]) || [];
        filename = 'expense_report';
        break;
      case 'pnl':
        headers = ['Metric', 'Value'];
        data = [
          ['Total Sales (Billed)', pnlReport?.revenue?.total_sales],
          ['Revenue Collected', pnlReport?.revenue?.collected_revenue],
          ['Pending Revenue', pnlReport?.revenue?.pending],
          ['Total Expenses (Accrued)', pnlReport?.expenses?.total],
          ['Fixed Expenses', pnlReport?.expenses?.fixed],
          ['Variable Expenses', pnlReport?.expenses?.variable],
          ['Expenses Paid', pnlReport?.expenses?.paid],
          ['Expenses Unpaid', pnlReport?.expenses?.unpaid ?? (pnlReport?.expenses?.total - pnlReport?.expenses?.paid)],
          [pnlMetrics.isProfitable ? 'Net Profit (Accrual)' : 'Net Loss (Accrual)', pnlMetrics.operatingProfit],
          ['Cash Profit (Collected vs Paid)', pnlMetrics.cashProfit],
          [pnlMetrics.isProfitable ? 'Net Margin' : 'Loss Margin', `${pnlMetrics.operatingMargin.toFixed(2)}%`],
          ['Expense Coverage Ratio', `${pnlMetrics.expenseCoverage.toFixed(2)}%`],
          ...(pnlMetrics.isProfitable ? [] : [['Breakeven Gap (Revenue Needed)', pnlMetrics.breakevenGap]]),
        ];
        filename = 'pnl_report';
        break;
      default:
        return;
    }
    
    const csv = [headers, ...data].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}_${startDate}_${endDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="flex min-h-screen bg-slate-50" data-testid="financial-reports">
      <Sidebar />
      <div className="flex-1 flex flex-col ml-[260px]">
        <TopHeader />
        <main className="flex-1 p-6 overflow-auto">
          {/* Header */}
          <div className="flex items-center gap-4 mb-6">
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin/accounting')}>
              <ChevronLeft size={18} />
            </Button>
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-slate-800">Financial Reports</h1>
              <p className="text-sm text-slate-500">Detailed analytics and insights</p>
            </div>
          </div>

          {/* Date Range Selector */}
          <Card className="border-0 shadow-sm mb-6">
            <CardContent className="p-4">
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-2">
                  <Label className="text-sm text-slate-500">From</Label>
                  <Input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-40"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Label className="text-sm text-slate-500">To</Label>
                  <Input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-40"
                  />
                </div>
                <div className="flex gap-2 ml-4">
                  <Button variant="outline" size="sm" onClick={() => setPreset('today')}>Today</Button>
                  <Button variant="outline" size="sm" onClick={() => setPreset('week')}>This Week</Button>
                  <Button variant="outline" size="sm" onClick={() => setPreset('month')}>This Month</Button>
                  <Button variant="outline" size="sm" onClick={() => setPreset('quarter')}>This Quarter</Button>
                  <Button variant="outline" size="sm" onClick={() => setPreset('year')}>This Year</Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Report Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="bg-white border">
              <TabsTrigger value="pnl" className="gap-2 data-[state=active]:bg-teal-50 data-[state=active]:text-teal-700">
                <BarChart3 size={16} />
                Profit & Loss
              </TabsTrigger>
              <TabsTrigger value="sales" className="gap-2 data-[state=active]:bg-teal-50 data-[state=active]:text-teal-700">
                <TrendingUp size={16} />
                Sales Report
              </TabsTrigger>
              <TabsTrigger value="expenses" className="gap-2 data-[state=active]:bg-teal-50 data-[state=active]:text-teal-700">
                <TrendingDown size={16} />
                Expense Report
              </TabsTrigger>
              <TabsTrigger value="outstanding" className="gap-2 data-[state=active]:bg-teal-50 data-[state=active]:text-teal-700">
                <Target size={16} />
                Outstanding
              </TabsTrigger>
              <TabsTrigger value="monthly" className="gap-2 data-[state=active]:bg-teal-50 data-[state=active]:text-teal-700">
                <Calendar size={16} />
                Monthly
              </TabsTrigger>
            </TabsList>

            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-teal-600"></div>
              </div>
            ) : (
              <>
                {/* P&L Report */}
                <TabsContent value="pnl">
                  {/* Loss / Profit Alert Banner */}
                  {pnlReport && (
                    pnlMetrics.isProfitable ? (
                      <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-200 rounded-xl px-5 py-3 mb-5">
                        <CheckCircle size={17} className="text-emerald-500 flex-shrink-0" />
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-emerald-700">Operating at a profit</p>
                          <p className="text-xs text-emerald-600 mt-0.5">
                            Net margin: {pnlMetrics.operatingMargin.toFixed(1)}% &nbsp;·&nbsp;
                            Expense coverage: {pnlMetrics.expenseCoverage.toFixed(1)}% &nbsp;·&nbsp;
                            Cash profit: {formatCurrency(pnlMetrics.cashProfit)}
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl px-5 py-3.5 mb-5">
                        <AlertTriangle size={17} className="text-red-500 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-red-700">Operating at a net loss</p>
                          <p className="text-xs text-red-600 mt-0.5">
                            Revenue covers {pnlMetrics.expenseCoverage.toFixed(1)}% of expenses. &nbsp;
                            Collect {formatCurrency(pnlMetrics.breakevenGap)} more to break even. &nbsp;
                            Cash position: {pnlMetrics.cashProfit >= 0 ? '+' : ''}{formatCurrency(pnlMetrics.cashProfit)}
                          </p>
                        </div>
                      </div>
                    )
                  )}

                  <div className="grid grid-cols-3 gap-6">
                    {/* Revenue Card */}
                    <Card className="border-0 shadow-sm border-l-4 border-l-teal-500">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base flex items-center gap-2 text-teal-700">
                          <TrendingUp size={18} />
                          Revenue
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="text-3xl font-bold text-teal-600">
                          {formatCurrency(pnlReport?.revenue?.total_sales)}
                        </div>
                        <div className="space-y-3 pt-2 border-t">
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-500">Total Billed</span>
                            <span className="font-medium">{formatCurrency(pnlReport?.revenue?.total_sales)}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-500">Collected</span>
                            <span className="font-medium text-emerald-600">{formatCurrency(pnlReport?.revenue?.collected_revenue)}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-500">Pending</span>
                            <span className="font-medium text-amber-600">{formatCurrency(pnlReport?.revenue?.pending)}</span>
                          </div>
                          <div className="flex justify-between text-xs pt-1 border-t text-slate-400">
                            <span>Collection rate</span>
                            <span className="font-semibold text-slate-600">
                              {pnlReport?.revenue?.total_sales > 0
                                ? ((pnlReport.revenue.collected_revenue / pnlReport.revenue.total_sales) * 100).toFixed(1)
                                : 0}%
                            </span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Expenses Card */}
                    <Card className="border-0 shadow-sm border-l-4 border-l-rose-500">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base flex items-center gap-2 text-rose-700">
                          <TrendingDown size={18} />
                          Expenses
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="text-3xl font-bold text-rose-600">
                          {formatCurrency(pnlReport?.expenses?.total)}
                        </div>
                        <div className="space-y-3 pt-2 border-t">
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-500">Fixed</span>
                            <span className="font-medium">{formatCurrency(pnlReport?.expenses?.fixed)}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-500">Variable</span>
                            <span className="font-medium">{formatCurrency(pnlReport?.expenses?.variable)}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-500">Paid</span>
                            <span className="font-medium text-emerald-600">{formatCurrency(pnlReport?.expenses?.paid)}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-500">Unpaid</span>
                            <span className="font-medium text-amber-600">
                              {formatCurrency(
                                pnlReport?.expenses?.unpaid ??
                                ((pnlReport?.expenses?.total ?? 0) - (pnlReport?.expenses?.paid ?? 0))
                              )}
                            </span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Net Profit / Net Loss Card — dynamic based on result */}
                    <Card className={`border-0 shadow-sm border-l-4 ${pnlMetrics.isProfitable ? 'border-l-emerald-500' : 'border-l-red-500'}`}>
                      <CardHeader className="pb-2">
                        <CardTitle className={`text-base flex items-center gap-2 ${pnlMetrics.isProfitable ? 'text-emerald-700' : 'text-red-700'}`}>
                          {pnlMetrics.isProfitable
                            ? <TrendingUp size={18} />
                            : <TrendingDown size={18} />
                          }
                          {pnlMetrics.isProfitable ? 'Net Profit' : 'Net Loss'}
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className={`text-3xl font-bold ${pnlMetrics.isProfitable ? 'text-emerald-600' : 'text-red-600'}`}>
                          {formatCurrency(pnlMetrics.operatingProfit)}
                        </div>
                        <div className="space-y-2.5 pt-2 border-t">
                          {/* Gross Profit / Loss row */}
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-500">
                              {pnlMetrics.isProfitable ? 'Gross Profit' : 'Gross Loss'}
                            </span>
                            <span className={`font-medium ${pnlMetrics.isProfitable ? 'text-emerald-600' : 'text-red-600'}`}>
                              {formatCurrency(pnlMetrics.operatingProfit)}
                            </span>
                          </div>
                          {/* Net Margin */}
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-500">Net Margin</span>
                            <span className={`font-semibold ${pnlMetrics.operatingMargin >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                              {pnlMetrics.operatingMargin >= 0 ? '+' : ''}{pnlMetrics.operatingMargin.toFixed(1)}%
                            </span>
                          </div>
                          {/* Expense Coverage */}
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-500">Exp. Coverage</span>
                            <span className={`font-medium ${pnlMetrics.expenseCoverage >= 100 ? 'text-emerald-600' : 'text-amber-600'}`}>
                              {pnlMetrics.expenseCoverage.toFixed(1)}%
                            </span>
                          </div>
                          {/* Divider — Cash basis */}
                          <div className="pt-1 border-t">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-1.5">
                                <Wallet size={12} className="text-slate-400" />
                                <span className="text-xs text-slate-400">Cash Basis</span>
                              </div>
                              <span className={`text-xs font-semibold ${pnlMetrics.cashProfit >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                                {pnlMetrics.cashProfit >= 0 ? '+' : ''}{formatCurrency(pnlMetrics.cashProfit)}
                              </span>
                            </div>
                            <p className="text-[10px] text-slate-400 mt-0.5 leading-tight">
                              Collected vs paid-out only
                            </p>
                          </div>
                          {/* Breakeven gap — only when at a loss */}
                          {!pnlMetrics.isProfitable && (
                            <div className="flex justify-between text-xs pt-1 border-t">
                              <span className="text-red-500 font-medium">Breakeven gap</span>
                              <span className="font-bold text-red-600">{formatCurrency(pnlMetrics.breakevenGap)}</span>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  <div className="flex justify-end mt-4">
                    <Button variant="outline" className="gap-2" onClick={() => handleExport('pnl')}>
                      <Download size={16} />
                      Export P&L Report
                    </Button>
                  </div>
                </TabsContent>

                {/* Sales Report */}
                <TabsContent value="sales">
                  {reportErrors.sales && (
                    <div className="flex items-center gap-3 bg-red-50 border border-red-200 rounded-xl px-5 py-3 mb-4">
                      <AlertTriangle size={16} className="text-red-500 flex-shrink-0" />
                      <p className="text-sm text-red-700">Failed to load sales report: <span className="font-mono">{reportErrors.sales}</span></p>
                    </div>
                  )}
                  <div className="grid grid-cols-4 gap-4 mb-6">
                    <StatCard 
                      label="Total Sales" 
                      value={formatCurrency(salesReport?.summary?.total_sales)} 
                      icon={DollarSign} 
                      color="green" 
                    />
                    <StatCard 
                      label="Collected" 
                      value={formatCurrency(salesReport?.summary?.collected)} 
                      icon={TrendingUp} 
                      color="blue" 
                    />
                    <StatCard 
                      label="Pending" 
                      value={formatCurrency(salesReport?.summary?.pending)} 
                      icon={Target} 
                      color="orange" 
                    />
                    <StatCard 
                      label="Count" 
                      value={salesReport?.summary?.count || 0} 
                      subvalue="transactions"
                      icon={FileText} 
                      color="slate" 
                    />
                  </div>

                  <Card className="border-0 shadow-sm">
                    <CardHeader className="flex flex-row items-center justify-between pb-2">
                      <CardTitle className="text-base">Sales Transactions</CardTitle>
                      <Button variant="outline" size="sm" className="gap-2" onClick={() => handleExport('sales')}>
                        <Download size={14} />
                        Export
                      </Button>
                    </CardHeader>
                    <CardContent>
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b">
                              <th className="text-left py-2 px-3 text-xs font-semibold text-slate-600">Client</th>
                              <th className="text-left py-2 px-3 text-xs font-semibold text-slate-600">Product</th>
                              <th className="text-right py-2 px-3 text-xs font-semibold text-slate-600">Total</th>
                              <th className="text-right py-2 px-3 text-xs font-semibold text-slate-600">Received</th>
                              <th className="text-right py-2 px-3 text-xs font-semibold text-slate-600">Balance</th>
                              <th className="text-center py-2 px-3 text-xs font-semibold text-slate-600">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(salesReport?.data || []).slice(0, 10).map((sale, idx) => (
                              <tr key={idx} className="border-b border-slate-50 hover:bg-slate-50">
                                <td className="py-2 px-3 text-sm">{sale.client_name}</td>
                                <td className="py-2 px-3 text-sm text-slate-600">{sale.visa_type || sale.product || '-'}</td>
                                <td className="py-2 px-3 text-sm text-right">{formatCurrency(sale.total_amount)}</td>
                                <td className="py-2 px-3 text-sm text-right text-green-600">{formatCurrency(sale.amount_received)}</td>
                                <td className="py-2 px-3 text-sm text-right text-orange-600">{formatCurrency(sale.balance_due)}</td>
                                <td className="py-2 px-3 text-center">
                                  <span className={`px-2 py-0.5 rounded text-xs ${
                                    sale.payment_status === 'fully_paid' ? 'bg-green-100 text-green-700' :
                                    sale.payment_status === 'overdue' ? 'bg-red-100 text-red-700' :
                                    'bg-yellow-100 text-yellow-700'
                                  }`}>
                                    {sale.payment_status?.replace('_', ' ')}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {(salesReport?.data?.length || 0) > 10 && (
                          <div className="text-center py-3">
                            <Button variant="ghost" size="sm" onClick={() => navigate('/admin/accounting/sales')}>
                              View All {salesReport?.data?.length} Sales <ArrowRight size={14} className="ml-1" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Expense Report */}
                <TabsContent value="expenses">
                  {reportErrors.expenses && (
                    <div className="flex items-center gap-3 bg-red-50 border border-red-200 rounded-xl px-5 py-3 mb-4">
                      <AlertTriangle size={16} className="text-red-500 flex-shrink-0" />
                      <p className="text-sm text-red-700">Failed to load expense report: <span className="font-mono">{reportErrors.expenses}</span></p>
                    </div>
                  )}
                  <div className="grid grid-cols-4 gap-4 mb-6">
                    <StatCard 
                      label="Total Expenses" 
                      value={formatCurrency(expenseReport?.summary?.total_expenses)} 
                      icon={TrendingDown} 
                      color="red" 
                    />
                    <StatCard 
                      label="Fixed" 
                      value={formatCurrency(expenseReport?.summary?.fixed_expenses)} 
                      icon={Target} 
                      color="blue" 
                    />
                    <StatCard 
                      label="Variable" 
                      value={formatCurrency(expenseReport?.summary?.variable_expenses)} 
                      icon={PieChart} 
                      color="orange" 
                    />
                    <StatCard 
                      label="Count" 
                      value={expenseReport?.summary?.count || 0} 
                      subvalue="entries"
                      icon={FileText} 
                      color="slate" 
                    />
                  </div>

                  <div className="grid grid-cols-3 gap-6 mb-6">
                    <Card className="border-0 shadow-sm col-span-2">
                      <CardHeader className="flex flex-row items-center justify-between pb-2">
                        <CardTitle className="text-base">Expense Transactions</CardTitle>
                        <Button variant="outline" size="sm" className="gap-2" onClick={() => handleExport('expenses')}>
                          <Download size={14} />
                          Export
                        </Button>
                      </CardHeader>
                      <CardContent>
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead>
                              <tr className="border-b">
                                <th className="text-left py-2 px-3 text-xs font-semibold text-slate-600">Title</th>
                                <th className="text-left py-2 px-3 text-xs font-semibold text-slate-600">Category</th>
                                <th className="text-center py-2 px-3 text-xs font-semibold text-slate-600">Type</th>
                                <th className="text-right py-2 px-3 text-xs font-semibold text-slate-600">Amount</th>
                                <th className="text-left py-2 px-3 text-xs font-semibold text-slate-600">Date</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(expenseReport?.data || []).slice(0, 8).map((exp, idx) => (
                                <tr key={idx} className="border-b border-slate-50 hover:bg-slate-50">
                                  <td className="py-2 px-3 text-sm">{exp.title}</td>
                                  <td className="py-2 px-3 text-sm text-slate-600 capitalize">{exp.category?.replace('_', ' ')}</td>
                                  <td className="py-2 px-3 text-center">
                                    <span className={`px-2 py-0.5 rounded text-xs ${
                                      exp.expense_type === 'fixed' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                                    }`}>
                                      {exp.expense_type}
                                    </span>
                                  </td>
                                  <td className="py-2 px-3 text-sm text-right font-medium">{formatCurrency(exp.amount)}</td>
                                  <td className="py-2 px-3 text-sm text-slate-500">{exp.date}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          {(expenseReport?.data?.length || 0) > 8 && (
                            <div className="text-center py-3">
                              <Button variant="ghost" size="sm" onClick={() => navigate('/admin/accounting/expenses')}>
                                View All {expenseReport?.data?.length} Expenses <ArrowRight size={14} className="ml-1" />
                              </Button>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="border-0 shadow-sm">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base flex items-center gap-2">
                          <PieChart size={16} className="text-teal-600" />
                          By Category
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {Object.entries(expenseReport?.by_category || {}).slice(0, 6).map(([cat, amount]) => (
                          <CategoryBar 
                            key={cat} 
                            label={cat} 
                            value={amount} 
                            total={expenseReport?.summary?.total_expenses} 
                          />
                        ))}
                      </CardContent>
                    </Card>
                  </div>
                </TabsContent>

                {/* Outstanding Report */}
                <TabsContent value="outstanding">
                  {/* API error banner — visible when auth or network fails */}
                  {reportErrors.outstanding && (
                    <div className="flex items-center gap-3 bg-red-50 border border-red-200 rounded-xl px-5 py-3 mb-4">
                      <AlertTriangle size={16} className="text-red-500 flex-shrink-0" />
                      <p className="text-sm text-red-700">
                        Failed to load outstanding report: <span className="font-mono">{reportErrors.outstanding}</span>
                      </p>
                    </div>
                  )}
                  <div className="grid grid-cols-4 gap-4 mb-6">
                    <StatCard
                      label="Total Outstanding"
                      value={formatCurrency(outstandingReport?.summary?.total_outstanding)}
                      icon={Target}
                      color="orange"
                    />
                    <StatCard 
                      label="Overdue Amount" 
                      value={formatCurrency(outstandingReport?.summary?.overdue_amount)} 
                      icon={TrendingDown} 
                      color="red" 
                    />
                    <StatCard 
                      label="Pending Count" 
                      value={outstandingReport?.summary?.pending_count || 0} 
                      subvalue="invoices"
                      icon={FileText} 
                      color="slate" 
                    />
                    <StatCard 
                      label="Overdue Count" 
                      value={outstandingReport?.summary?.overdue_count || 0} 
                      subvalue="invoices"
                      icon={FileText} 
                      color="red" 
                    />
                  </div>

                  <Card className="border-0 shadow-sm">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base">Outstanding Payments</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {(outstandingReport?.data?.length || 0) === 0 ? (
                        <div className="text-center py-8 text-slate-500">
                          <Target size={48} className="mx-auto mb-3 text-slate-300" />
                          <p className="font-medium">
                            {reportErrors.outstanding ? 'Could not load data' : 'No outstanding payments'}
                          </p>
                          {!reportErrors.outstanding && (
                            <p className="text-xs text-slate-400 mt-1">All invoices are fully collected for this period</p>
                          )}
                        </div>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead>
                              <tr className="border-b">
                                <th className="text-left py-2 px-3 text-xs font-semibold text-slate-600">Client</th>
                                <th className="text-left py-2 px-3 text-xs font-semibold text-slate-600">Product</th>
                                <th className="text-right py-2 px-3 text-xs font-semibold text-slate-600">Total</th>
                                <th className="text-right py-2 px-3 text-xs font-semibold text-slate-600">Received</th>
                                <th className="text-right py-2 px-3 text-xs font-semibold text-slate-600">Balance</th>
                                <th className="text-left py-2 px-3 text-xs font-semibold text-slate-600">Due Date</th>
                                <th className="text-center py-2 px-3 text-xs font-semibold text-slate-600">Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(outstandingReport?.data || []).map((item, idx) => {
                                const isOverdue = item.payment_status === 'overdue';
                                return (
                                  <tr key={idx} className={`border-b border-slate-50 hover:bg-slate-50 ${isOverdue ? 'bg-red-50/30' : ''}`}>
                                    <td className="py-2 px-3 text-sm font-medium">{item.client_name}</td>
                                    <td className="py-2 px-3 text-sm text-slate-600">{item.visa_type || item.product || '-'}</td>
                                    <td className="py-2 px-3 text-sm text-right">{formatCurrency(item.total_amount)}</td>
                                    <td className="py-2 px-3 text-sm text-right text-green-600">{formatCurrency(item.amount_received)}</td>
                                    <td className="py-2 px-3 text-sm text-right font-semibold text-orange-600">{formatCurrency(item.balance_due)}</td>
                                    <td className="py-2 px-3 text-sm text-slate-500">{item.due_date || '-'}</td>
                                    <td className="py-2 px-3 text-center">
                                      <span className={`px-2 py-0.5 rounded text-xs ${isOverdue ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>
                                        {isOverdue ? 'OVERDUE' : 'PENDING'}
                                      </span>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Monthly Expense Report with Carry Forward */}
                <TabsContent value="monthly">
                  {/* Month Selector */}
                  <div className="flex items-center gap-4 mb-6">
                    <Label className="text-sm font-medium text-slate-600">Select Month:</Label>
                    <Input
                      type="month"
                      value={selectedMonth}
                      onChange={(e) => setSelectedMonth(e.target.value)}
                      className="w-48"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-6">
                    {/* Section 1: Current Month Expenses */}
                    <Card className="border-0 shadow-sm">
                      <CardHeader className="pb-3 border-b">
                        <CardTitle className="text-base flex items-center gap-2">
                          <FileText size={18} className="text-teal-600" />
                          Current Month Expenses
                        </CardTitle>
                        <p className="text-xs text-slate-500">
                          Expenses incurred in {selectedMonth} (excluding carry forwards)
                        </p>
                      </CardHeader>
                      <CardContent className="pt-4">
                        {/* Summary Cards */}
                        <div className="grid grid-cols-3 gap-3 mb-4">
                          <div className="bg-slate-50 rounded-lg p-3 text-center">
                            <p className="text-xs text-slate-500">Total</p>
                            <p className="text-lg font-bold text-slate-800">
                              {formatCurrency(monthlyReport?.current_month?.summary?.total || 0)}
                            </p>
                          </div>
                          <div className="bg-green-50 rounded-lg p-3 text-center">
                            <p className="text-xs text-green-600">Paid</p>
                            <p className="text-lg font-bold text-green-700">
                              {formatCurrency(monthlyReport?.current_month?.summary?.paid || 0)}
                            </p>
                          </div>
                          <div className="bg-orange-50 rounded-lg p-3 text-center">
                            <p className="text-xs text-orange-600">Due</p>
                            <p className="text-lg font-bold text-orange-700">
                              {formatCurrency(monthlyReport?.current_month?.summary?.due || 0)}
                            </p>
                          </div>
                        </div>

                        {/* Expense List */}
                        <div className="max-h-[300px] overflow-y-auto">
                          {(monthlyReport?.current_month?.expenses || []).length === 0 ? (
                            <div className="text-center py-8 text-slate-400">
                              No expenses for this month
                            </div>
                          ) : (
                            <table className="w-full text-sm">
                              <thead className="sticky top-0 bg-white">
                                <tr className="border-b">
                                  <th className="text-left py-2 text-xs font-semibold text-slate-600">Title</th>
                                  <th className="text-left py-2 text-xs font-semibold text-slate-600">Category</th>
                                  <th className="text-right py-2 text-xs font-semibold text-slate-600">Amount</th>
                                  <th className="text-center py-2 text-xs font-semibold text-slate-600">Status</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(monthlyReport?.current_month?.expenses || []).map((expense, idx) => (
                                  <tr key={idx} className="border-b border-slate-50">
                                    <td className="py-2 font-medium">{expense.title}</td>
                                    <td className="py-2 text-slate-500 capitalize">{(expense.category || '').replace('_', ' ')}</td>
                                    <td className="py-2 text-right">{formatCurrency(expense.amount)}</td>
                                    <td className="py-2 text-center">
                                      <span className={`px-2 py-0.5 rounded text-xs ${expense.is_paid ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`}>
                                        {expense.is_paid ? 'Paid' : 'Due'}
                                      </span>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </div>
                      </CardContent>
                    </Card>

                    {/* Section 2: Carry Forward Expenses */}
                    <Card className="border-0 shadow-sm border-l-4 border-l-purple-500">
                      <CardHeader className="pb-3 border-b">
                        <CardTitle className="text-base flex items-center gap-2">
                          <ArrowRight size={18} className="text-purple-600" />
                          Carry Forward Expenses
                        </CardTitle>
                        <p className="text-xs text-slate-500">
                          Unpaid expenses carried forward from previous months
                        </p>
                      </CardHeader>
                      <CardContent className="pt-4">
                        {/* Summary Cards */}
                        <div className="grid grid-cols-3 gap-3 mb-4">
                          <div className="bg-purple-50 rounded-lg p-3 text-center">
                            <p className="text-xs text-purple-600">Total CF</p>
                            <p className="text-lg font-bold text-purple-700">
                              {formatCurrency(monthlyReport?.carry_forward?.summary?.total || 0)}
                            </p>
                          </div>
                          <div className="bg-green-50 rounded-lg p-3 text-center">
                            <p className="text-xs text-green-600">Paid</p>
                            <p className="text-lg font-bold text-green-700">
                              {formatCurrency(monthlyReport?.carry_forward?.summary?.paid || 0)}
                            </p>
                          </div>
                          <div className="bg-red-50 rounded-lg p-3 text-center">
                            <p className="text-xs text-red-600">Still Due</p>
                            <p className="text-lg font-bold text-red-700">
                              {formatCurrency(monthlyReport?.carry_forward?.summary?.due || 0)}
                            </p>
                          </div>
                        </div>

                        {/* Expense List */}
                        <div className="max-h-[300px] overflow-y-auto">
                          {(monthlyReport?.carry_forward?.expenses || []).length === 0 ? (
                            <div className="text-center py-8 text-slate-400">
                              No carry forward expenses
                            </div>
                          ) : (
                            <table className="w-full text-sm">
                              <thead className="sticky top-0 bg-white">
                                <tr className="border-b">
                                  <th className="text-left py-2 text-xs font-semibold text-slate-600">Title</th>
                                  <th className="text-left py-2 text-xs font-semibold text-slate-600">From</th>
                                  <th className="text-right py-2 text-xs font-semibold text-slate-600">Amount</th>
                                  <th className="text-center py-2 text-xs font-semibold text-slate-600">Status</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(monthlyReport?.carry_forward?.expenses || []).map((expense, idx) => (
                                  <tr key={idx} className="border-b border-slate-50 bg-purple-50/30">
                                    <td className="py-2 font-medium">{expense.title}</td>
                                    <td className="py-2 text-purple-600 text-xs">{expense.carry_forward_from_month || '-'}</td>
                                    <td className="py-2 text-right">{formatCurrency(expense.amount)}</td>
                                    <td className="py-2 text-center">
                                      <span className={`px-2 py-0.5 rounded text-xs ${expense.is_paid ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                        {expense.is_paid ? 'Paid' : 'Due'}
                                      </span>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Total Liability Summary */}
                  <Card className="border-0 shadow-sm mt-6 bg-gradient-to-r from-slate-800 to-slate-700 text-white">
                    <CardContent className="p-6">
                      <div className="grid grid-cols-4 gap-6 text-center">
                        <div>
                          <p className="text-slate-300 text-sm mb-1">Current Month Total</p>
                          <p className="text-2xl font-bold">{formatCurrency(monthlyReport?.current_month?.summary?.total || 0)}</p>
                        </div>
                        <div>
                          <p className="text-slate-300 text-sm mb-1">Carry Forward Total</p>
                          <p className="text-2xl font-bold">{formatCurrency(monthlyReport?.carry_forward?.summary?.total || 0)}</p>
                        </div>
                        <div>
                          <p className="text-slate-300 text-sm mb-1">Combined Expenses</p>
                          <p className="text-2xl font-bold">
                            {formatCurrency(monthlyReport?.totals?.total_expenses || 0)}
                          </p>
                        </div>
                        <div className="border-l border-slate-600 pl-6">
                          <p className="text-orange-300 text-sm mb-1">Total Outstanding Liability</p>
                          <p className="text-3xl font-bold text-orange-400">
                            {formatCurrency(monthlyReport?.totals?.total_outstanding || 0)}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>
              </>
            )}
          </Tabs>
        </main>
      </div>
    </div>
  );
}
